import { useState, type FormEvent } from "react";
import { LoaderCircle, LockKeyhole } from "lucide-react";

import type { SessionInfo } from "../../shared/contracts";
import { AuthFrame } from "../components/auth-frame";
import { Button } from "../components/ui/button";
import { Field, FieldError, FieldGroup, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { api, getErrorMessage } from "../lib/api";

export function LoginPage({ onComplete }: { onComplete: (session: SessionInfo) => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      onComplete(await api.login(password));
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame title="Welcome back" description="Sign in to review the ETH/USD strategy, account state, and live controls.">
      <form className="grid gap-6" onSubmit={(event) => void submit(event)}>
        <FieldGroup>
          <Field data-invalid={Boolean(error) || undefined}>
            <FieldLabel htmlFor="password">Control-room password</FieldLabel>
            <div className="relative">
              <LockKeyhole className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                className="pl-10"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                aria-invalid={Boolean(error)}
                autoFocus
                required
              />
            </div>
          </Field>
        </FieldGroup>
        {error && <FieldError>{error}</FieldError>}
        <Button type="submit" variant="signal" size="lg" disabled={busy} className="w-full">
          {busy && <LoaderCircle data-icon="inline-start" className="animate-spin" />}
          Sign in
        </Button>
      </form>
    </AuthFrame>
  );
}
